/**
 * AutomationRuleBuilderContainer — form state + save wiring for the rule builder
 * (SE-5506).
 *
 * Owns the whole builder form. Seeds it once from the initializer (create → a
 * blank form; edit → the rule mapped back via `ruleToForm`); a fresh form per
 * open is guaranteed by the KEY-RESET wrapper at the call site (the repo's
 * sanctioned alternative to a set-state-in-effect reset), so nothing here
 * mutates state from an effect.
 *
 * The live preview and the Save gate are pure functions of the form
 * (`buildAutomationRulePreview` / `isAutomationRuleFormComplete`), and every
 * option list is fed from the live enums / board data — the View never
 * hardcodes a subset. `numericValue` is captured as a raw string and forwarded
 * untouched through `useAutomationRuleCrud`.
 * @remarks Container component — owns form state, derivations, and the mutation.
 */

import { memo, useCallback, useMemo, useRef, useState } from "react";

import {
  type KanbanCardOutcome,
  PipelineAutomationActionType,
  type PipelineAutomationRuleFragmentFragment,
  type PipelineAutomationTriggerType,
} from "@/generated/graphql";
import { useToastNotification } from "@/hooks/useToastNotification";
import useMobileScreen from "@/hooks/useMobileScreen";

import { MESSAGES } from "../../../constants";
import { useAutomationRuleCrud } from "../../../hooks/useAutomationRuleCrud";
import type {
  AutomationRuleConditionForm,
  AutomationRuleKind,
  RuleStageOption,
  RuleTagOption,
} from "../../../types/automationRules";
import {
  makeEmptyAutomationRuleForm,
  makeEmptyCondition,
  ruleToForm,
} from "../../../utils/automationRuleForm";
import {
  ACTION_OPTIONS,
  COMPARATOR_OPTIONS,
  CONDITION_TYPE_OPTIONS,
  TRIGGER_OPTIONS,
  actionNeedsTag,
  actionNeedsTargetStage,
  actionSupportsOutcome,
  triggerNeedsStage,
} from "../../../utils/automationRuleOptions";
import { buildAutomationRulePreview } from "../../../utils/automationRulePreview";
import { isAutomationRuleFormComplete } from "../../../utils/automationRuleValidation";
import { outcomeLabel } from "../../../utils/outcomeLabels";
import type { RuleSelectOption } from "../../molecules/RuleSelectField";
import AutomationRuleBuilderView from "./AutomationRuleBuilderView";

/** Props for {@link AutomationRuleBuilderContainer}. */
export interface AutomationRuleBuilderContainerProps {
  /** Whether the modal is open. */
  readonly isOpen: boolean;
  /** The pipeline the rule belongs to. */
  readonly boardId: string;
  /** Rule being edited, or undefined to create a new one. */
  readonly editingRule?: PipelineAutomationRuleFragmentFragment;
  /**
   * Which Kind a NEW rule starts on. Ignored when editing — an existing rule's
   * kind is derived from its own trigger/action pair.
   *
   * The panel offers two entry points ("+ Rule" and "+ Automation") and this is
   * what makes them differ: each opens the builder on the kind it names. There
   * is no Kind picker inside the form, so this is the ONLY thing that decides
   * the shape of a new rule — it is a decision, not a default.
   */
  readonly initialKind?: AutomationRuleKind;
  /** Board stages (ordered), for the stage pickers + terminal detection. */
  readonly stages: readonly RuleStageOption[];
  /** Board tags, for the apply/remove-tag pickers. */
  readonly tags: readonly RuleTagOption[];
  /** Board template's valid terminal outcomes. */
  readonly validOutcomes: readonly KanbanCardOutcome[];
  /** Close request handler. */
  readonly onClose: () => void;
  /** Invoked after a successful create/update (before the modal closes). */
  readonly onSaved?: () => void;
}

/**
 * Container for the rule builder modal.
 * @param props - {@link AutomationRuleBuilderContainerProps}
 * @param props.isOpen - Whether the modal is open
 * @param props.boardId - The pipeline the rule belongs to
 * @param props.editingRule - Rule being edited, or undefined to create
 * @param props.initialKind - Kind a NEW rule starts on (ignored when editing)
 * @param props.stages - Board stages for the pickers + terminal detection
 * @param props.tags - Board tags for the apply/remove-tag pickers
 * @param props.validOutcomes - Board template's valid terminal outcomes
 * @param props.onClose - Close request handler
 * @param props.onSaved - Invoked after a successful create/update
 * @returns The wired {@link AutomationRuleBuilderView}.
 */
const AutomationRuleBuilderContainer = ({
  isOpen,
  boardId,
  editingRule,
  initialKind,
  stages,
  tags,
  validOutcomes,
  onClose,
  onSaved,
}: AutomationRuleBuilderContainerProps) => {
  const isMobile = useMobileScreen();
  const toast = useToastNotification();
  const { createRule, updateRule, isCreating, isUpdating } =
    useAutomationRuleCrud(boardId);

  // Seed once — the call-site key remounts on a new open, so no reset effect.
  const [form, setForm] = useState(() =>
    editingRule
      ? ruleToForm(editingRule, index => `c${index}`)
      : makeEmptyAutomationRuleForm(initialKind)
  );

  // Monotonic condition-key source (mutated only in event handlers).
  const keyCounter = useRef(editingRule?.conditions.length ?? 0);

  const stageOptions = useMemo<readonly RuleSelectOption[]>(
    () => stages.map(stage => ({ value: stage.id, label: stage.name })),
    [stages]
  );
  const tagOptions = useMemo<readonly RuleSelectOption[]>(
    () => tags.map(tag => ({ value: tag.id, label: tag.name })),
    [tags]
  );
  const outcomeOptions = useMemo<readonly RuleSelectOption[]>(
    () => validOutcomes.map(o => ({ value: o, label: outcomeLabel(o) })),
    [validOutcomes]
  );

  const lookups = useMemo(
    () => ({
      phaseName: (id: string | null | undefined) =>
        stages.find(stage => stage.id === id)?.name ?? "…",
      tagName: (id: string | null | undefined) =>
        tags.find(tag => tag.id === id)?.name ?? "…",
    }),
    [stages, tags]
  );

  const preview = useMemo(
    () => buildAutomationRulePreview(form, lookups),
    [form, lookups]
  );

  const showTargetStage = actionNeedsTargetStage(form.actionType);
  const targetIsTerminal = useMemo(
    () =>
      stages.find(stage => stage.id === form.targetKanbanPhaseId)?.isTerminal ??
      false,
    [stages, form.targetKanbanPhaseId]
  );
  const showOutcome =
    form.kind === "automation" &&
    actionSupportsOutcome(form.actionType) &&
    targetIsTerminal;

  /*
   * The title names the KIND, not just the operation. With the Kind picker
   * gone, this heading is the only thing on screen that says which of the two
   * you are authoring.
   */
  const title = `${editingRule ? "Edit" : "New"} ${
    form.kind === "guard" ? "rule" : "automation"
  }`;

  const canSave =
    isAutomationRuleFormComplete(form) && Boolean(boardId) && stages.length > 0;
  const isSaving = isCreating || isUpdating;

  // --- Field handlers -------------------------------------------------------

  const handleNameChange = useCallback(
    (name: string) => setForm(prev => ({ ...prev, name })),
    []
  );
  const handleTriggerChange = useCallback(
    (value: string) =>
      setForm(prev => ({
        ...prev,
        triggerType: value as PipelineAutomationTriggerType,
      })),
    []
  );
  const handleTriggerStageChange = useCallback(
    (value: string) =>
      setForm(prev => ({ ...prev, triggerKanbanPhaseId: value })),
    []
  );
  const handleActionChange = useCallback(
    (value: string) =>
      setForm(prev => ({
        ...prev,
        actionType: value as PipelineAutomationActionType,
      })),
    []
  );
  const handleTargetStageChange = useCallback(
    (value: string) =>
      setForm(prev => {
        // Mirror `showOutcome`: an outcome is only valid for a move into a
        // TERMINAL stage. Drop a previously-chosen outcome the moment the target
        // stops being terminal so a stale outcome can neither show in the
        // preview nor ride into the built input.
        const isTerminal =
          stages.find(stage => stage.id === value)?.isTerminal ?? false;
        return {
          ...prev,
          targetKanbanPhaseId: value,
          ...(isTerminal ? {} : { requestedOutcome: null }),
        };
      }),
    [stages]
  );
  const handleTagChange = useCallback(
    (value: string) => setForm(prev => ({ ...prev, kanbanTagId: value })),
    []
  );
  const handleOutcomeChange = useCallback(
    (value: string) =>
      setForm(prev => ({
        ...prev,
        requestedOutcome: value as KanbanCardOutcome,
      })),
    []
  );
  const handleGuardStageChange = useCallback(
    (value: string) =>
      setForm(prev => ({ ...prev, triggerKanbanPhaseId: value })),
    []
  );

  // --- Condition handlers ---------------------------------------------------

  const handleAddCondition = useCallback(() => {
    const key = `c${keyCounter.current}`;
    keyCounter.current += 1;
    setForm(prev => ({
      ...prev,
      conditions: [...prev.conditions, makeEmptyCondition(key)],
    }));
  }, []);

  const handleConditionChange = useCallback(
    (key: string, patch: Partial<AutomationRuleConditionForm>) =>
      setForm(prev => ({
        ...prev,
        conditions: prev.conditions.map(condition =>
          condition.key === key ? { ...condition, ...patch } : condition
        ),
      })),
    []
  );

  const handleConditionRemove = useCallback(
    (key: string) =>
      setForm(prev => ({
        ...prev,
        conditions: prev.conditions.filter(condition => condition.key !== key),
      })),
    []
  );

  // --- Save -----------------------------------------------------------------

  const handleSave = useCallback(async () => {
    try {
      if (editingRule) {
        await updateRule(editingRule.id, form);
      } else {
        await createRule(form);
      }
      toast.show({
        action: "success",
        description: editingRule
          ? MESSAGES.SUCCESS.UPDATED
          : MESSAGES.SUCCESS.CREATED,
      });
      onSaved?.();
      onClose();
    } catch {
      toast.show({ action: "danger", description: MESSAGES.ERROR.GENERIC });
    }
  }, [editingRule, form, updateRule, createRule, toast, onSaved, onClose]);

  return (
    <AutomationRuleBuilderView
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      isMobile={isMobile}
      kind={form.kind}
      name={form.name}
      onNameChange={handleNameChange}
      triggerOptions={TRIGGER_OPTIONS}
      triggerValue={form.triggerType}
      onTriggerChange={handleTriggerChange}
      showTriggerStage={triggerNeedsStage(form.triggerType)}
      triggerStageValue={form.triggerKanbanPhaseId ?? ""}
      onTriggerStageChange={handleTriggerStageChange}
      actionOptions={ACTION_OPTIONS}
      actionValue={form.actionType}
      onActionChange={handleActionChange}
      showTargetStage={showTargetStage}
      targetStageValue={form.targetKanbanPhaseId ?? ""}
      onTargetStageChange={handleTargetStageChange}
      showTag={actionNeedsTag(form.actionType)}
      tagOptions={tagOptions}
      tagValue={form.kanbanTagId ?? ""}
      onTagChange={handleTagChange}
      showOutcome={showOutcome}
      outcomeOptions={outcomeOptions}
      outcomeValue={form.requestedOutcome ?? ""}
      onOutcomeChange={handleOutcomeChange}
      stageOptions={stageOptions}
      guardStageValue={form.triggerKanbanPhaseId ?? ""}
      onGuardStageChange={handleGuardStageChange}
      conditions={form.conditions}
      conditionTypeOptions={CONDITION_TYPE_OPTIONS}
      comparatorOptions={COMPARATOR_OPTIONS}
      onAddCondition={handleAddCondition}
      onConditionChange={handleConditionChange}
      onConditionRemove={handleConditionRemove}
      preview={preview}
      canSave={canSave}
      isSaving={isSaving}
      onSave={handleSave}
    />
  );
};

AutomationRuleBuilderContainer.displayName = "AutomationRuleBuilderContainer";

export default memo(AutomationRuleBuilderContainer);
